import React, { useState, useEffect } from 'react';
import BaseModal from '../../../../components/modals/BaseModal';
import { useLoadingContext } from '../../../../context/loading/LoadingContext';
import { useUserContext } from '../../../../context/user_context/UserContext';
import { Grid, TextField, Button, FormControl, InputLabel, Select, MenuItem, IconButton, Typography } from '@mui/material';
import { useToast, ToastType } from '../../../../context/toast/ToastContext';
import { StringKeys } from '../../../../resources/strings/StringKeys';
import { useTranslation } from 'react-i18next';
import { updateInventoryOrder } from '../../../../repository/OrdersDAO';  // Update to use your update function
import { getInventoryItems } from '../../../../repository/InventoryDAO';  
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useInventoryContext } from '../../../../context/inventory_context/InventoryContext';

const EditInventoryOrderModal = ({ show, onHide, onEdit, order }) => {

    const createEmptyLineItem = () => ({
        inventory_item_id: "",
        quantity_ordered: 0,
        price_per_unit: 0
    });

    const [inventoryItems, setInventoryItems] = useState([]);
    const [lineItems, setLineItems] = useState(Array.isArray(order?.items) ? order.items : [createEmptyLineItem()]);
    const [supplier, setSupplier] = useState(order?.supplier || "");
    const [status, setStatus] = useState(order?.status || "pending");
    const [expectedDeliveryDate, setExpectedDeliveryDate] = useState(order ? new Date(order.expected_delivery_date.seconds * 1000).toISOString().split('T')[0] : "");
    const [totalCost, setTotalCost] = useState(order?.total_cost || 0);
    const [notes, setNotes] = useState(order?.notes || "");
    const [purchaseOrder, setPurchaseOrder] = useState("");
    const { setLoadingData } = useLoadingContext();
    const { firebaseUser } = useUserContext();
    const { suppliers } = useInventoryContext();
    const { showToast } = useToast();
    const { t } = useTranslation();

    useEffect(() => {

        const fetchInventoryItems = async () => {
            const items = await getInventoryItems(firebaseUser.organization);
            setInventoryItems(items);
        };

        fetchInventoryItems();
    }, [firebaseUser]);

    const handleAddLineItem = () => {
        setLineItems([...lineItems, createEmptyLineItem()]);
    };

    const handleRemoveLineItem = (index) => {
        const newLineItems = lineItems.filter((_, i) => i !== index);
        setLineItems(newLineItems);
    };

    const handleLineItemChange = (index, key, value) => {
        const newLineItems = [...lineItems];
        newLineItems[index][key] = value;
        setLineItems(newLineItems);
        calculateTotalCost(newLineItems);
    };

    const calculateTotalCost = (items) => {
        const total = items.reduce((acc, item) => acc + (item.quantity_ordered * item.price_per_unit), 0);
        setTotalCost(total);
    };

    const handleSubmit = async () => {
        setLoadingData(true);
        try {
            const updatedOrder = {
                supplier,
                status,
                order_date: order.order_date,
                expected_delivery_date: new Date(expectedDeliveryDate),
                total_cost: totalCost,
                created_by: order.created_by,
                notes,
                items: lineItems
            };
    
            const result = await updateInventoryOrder(firebaseUser.organization, order.id, updatedOrder);
    
            if (result.success) {
                showToast(StringKeys.SUCCESS, ToastType.SUCCESS);
                if (onEdit) onEdit();
                onHide();
            } else {
                showToast(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN, ToastType.DANGER);
            }
        } catch (error) {
            showToast(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN, ToastType.DANGER);
        } finally {
            setLoadingData(false);
        }
    };
    

    const actions = (
        <Button onClick={handleSubmit}>{t(StringKeys.SUBMIT)}</Button>
    );

    return (
        <BaseModal
            open={show}
            onClose={onHide}
            title={t(StringKeys.EDIT_ORDER)}
            actions={actions}
        >
            <Grid container spacing={2}>
                <Grid item xs={8}>
                    <FormControl fullWidth margin="dense">
                        <InputLabel id="supplier-label">{t(StringKeys.SUPPLIER)}</InputLabel>
                        <Select
                            labelId="supplier-label"
                            id="supplier-select"
                            value={supplier}
                            label={t(StringKeys.SUPPLIER)}
                            onChange={(e) => setSupplier(e.target.value)}
                        >
                            {suppliers.map((supplier) => (
                                <MenuItem key={supplier.name} value={supplier.name}>{supplier.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={4}>
                    <TextField
                        margin="dense"
                        id="purchase-order"
                        label={t(StringKeys.PURCHASE_ORDER)}
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={purchaseOrder}
                        onChange={(e) => setPurchaseOrder(e.target.value)}
                    />
                </Grid>

                {Array.isArray(lineItems) && lineItems.length > 0 ? (
                    lineItems.map((item, index) => (
                        <Grid container item xs={12} spacing={2} key={index}>
                            <Grid item xs={4}>
                                <FormControl fullWidth margin="dense">
                                    <InputLabel id={`item-label-${index}`}>{t(StringKeys.ITEM_NAME)}</InputLabel>
                                    <Select
                                        labelId={`item-label-${index}`}
                                        id={`item-select-${index}`}
                                        value={item.inventory_item_id}
                                        label={t(StringKeys.ITEM_NAME)}
                                        onChange={(e) => handleLineItemChange(index, 'inventory_item_id', e.target.value)}
                                    >
                                        {inventoryItems.map((invItem) => (
                                            <MenuItem key={invItem.id} value={invItem.id}>{invItem.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={3}>
                                <TextField
                                    margin="dense"
                                    id={`quantity-${index}`}
                                    label={t(StringKeys.QUANTITY)}
                                    type="number"
                                    InputProps={{ inputProps: { min: "0", step: "1" } }}
                                    fullWidth
                                    variant="outlined"
                                    value={item.quantity_ordered}
                                    onChange={(e) => handleLineItemChange(index, 'quantity_ordered', e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TextField
                                    margin="dense"
                                    id={`price-${index}`}
                                    label={t(StringKeys.PRICE_PER_UNIT)}
                                    type="number"
                                    InputProps={{ inputProps: { min: "0", step: "0.01" } }}
                                    fullWidth
                                    variant="outlined"
                                    value={item.price_per_unit}
                                    onChange={(e) => handleLineItemChange(index, 'price_per_unit', e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={2} container alignItems="center" justifyContent="center">
                                <IconButton onClick={() => handleRemoveLineItem(index)}>
                                    <IconTrash style={{color:"red"}} />
                                </IconButton>
                            </Grid>
                        </Grid>
                    ))
                ) : (
                    <Typography>{t(StringKeys.NO_ITEMS_ORDERED)}</Typography>
                )}

                <Grid item xs={12}>
                    <Button onClick={handleAddLineItem}>
                        <IconPlus /> {t(StringKeys.ADD_LINE_ITEM)}
                    </Button>
                </Grid>
            </Grid>
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <TextField
                        margin="dense"
                        id="expectedDeliveryDate"
                        label={t(StringKeys.EXPECTED_DELIVERY_DATE)}
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        variant="outlined"
                        value={expectedDeliveryDate}
                        onChange={(e) => setExpectedDeliveryDate(e.target.value)}
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        margin="dense"
                        id="totalCost"
                        label={t(StringKeys.TOTAL_COST)}
                        type="number"
                        InputProps={{ inputProps: { min: "0", step: "0.01" }, readOnly: true }}
                        fullWidth
                        variant="outlined"
                        value={totalCost}
                    />
                </Grid>
            </Grid>
            <TextField
                margin="dense"
                id="notes"
                label={t(StringKeys.NOTES)}
                type="text"
                fullWidth
                variant="outlined"
                multiline
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
            />
        </BaseModal>
    );
};

export default EditInventoryOrderModal;
